export interface IMegaMenuTreeNode {
	id: string
	title_pl: string
	title_en: string
	subtext_pl?: string | null
	subtext_en?: string | null
	icon?: string | null
	link?: string | null
	children: IMegaMenuTreeNode[]
}

export const createEmptyMegaMenuNode = (overrides: Partial<IMegaMenuTreeNode> = {}): IMegaMenuTreeNode => ({
	id: crypto.randomUUID(),
	title_pl: '',
	title_en: '',
	subtext_pl: null,
	subtext_en: null,
	icon: null,
	link: null,
	children: [],
	...overrides,
})

// Fixed set of mega-menu sections - the "Oferta" dropdown always has exactly these three,
// in this order, and they are NOT admin-editable or admin-creatable - only their links (and
// links' sublinks) are. Section id/title live only here, in code, never in the saved data.
export const MEGA_MENU_SECTION_DEFS: { id: string; title_pl: string; title_en: string }[] = [
	{ id: 'rodzaj-wynajmu', title_pl: 'Rodzaj wynajmu', title_en: 'Type of rental' },
	{ id: 'kategorie-pojazdow', title_pl: 'Kategorie pojazdów', title_en: 'Vehicle categories' },
	{ id: 'lokalizacje', title_pl: 'Lokalizacje', title_en: 'Locations' },
]

// Links whose id carries extra frontend-only business logic (dynamic CMSLocation injection
// for cities/airports, GTM category tagging, car-search filters - see MEGA_MENU_LINK_META in
// src/components/Global/MegaMenu/megaMenuContentMapper.ts). A freehand "empty element" gets a
// random crypto.randomUUID() id and would silently lose all of that behavior, so the admin
// picker offers these as ready-made entries instead, with the id/title/icon/link that matches
// what the frontend logic expects.
export const MEGA_MENU_SPECIAL_LINK_DEFS: { id: string; title_pl: string; title_en: string; icon: string; link: string }[] = [
	{ id: 'bus-rental', title_pl: 'Wynajem busów osobowych', title_en: 'Passenger bus rental', icon: 'bus', link: 'bus-rental' },
	{
		id: 'delivery-van-rental',
		title_pl: 'Wynajem Busa Dostawczego',
		title_en: 'Delivery bus rental',
		icon: 'delivery-bus',
		link: 'delivery-bus-rental',
	},
	{ id: 'cities', title_pl: 'Miasta', title_en: 'Cities', icon: 'location', link: 'locations' },
	{ id: 'airports', title_pl: 'Lotniska', title_en: 'Airports', icon: 'airplane-outline', link: 'locations' },
]

// The saved content shape: just the three sections' link lists, keyed by section id.
// No section metadata is stored - MEGA_MENU_SECTION_DEFS is the only source of truth for that.
export type IMegaMenuLinksBySection = Record<string, IMegaMenuTreeNode[]>

// Takes whatever was loaded from the content-section (may be empty, may be missing a key
// from an earlier iteration) and normalizes it to exactly one array per MEGA_MENU_SECTION_DEFS
// entry, keyed by id.
export const normalizeMegaMenuLinksBySection = (loaded: IMegaMenuLinksBySection | null | undefined): IMegaMenuLinksBySection =>
	Object.fromEntries(MEGA_MENU_SECTION_DEFS.map((def) => [def.id, loaded?.[def.id] ?? []]))
